import { useMemo } from 'react';

import { VendorTaxonomyKey } from '@zola-helpers/client/dist/es/marketplace/vendorTaxonomyKeys';
import { getVendorType } from '@zola-helpers/client/dist/es/marketplace/vendorUtils';

import { useUserContext } from '~/contexts/UserContext';
import { useAppSelector } from '~/reducers';
import { AccountVendorView } from '~/types/responseTypes';

export const getBookedVendorsAllowsMiniQuiz = (
  accountVendors: AccountVendorView[],
  taxonomyKey: VendorTaxonomyKey | undefined
): boolean => {
  if (!taxonomyKey) {
    return false;
  }
  const hasBookedCategory = Boolean(
    accountVendors.find((accountVendor) => {
      return accountVendor.booked && accountVendor.vendorType === getVendorType(taxonomyKey);
    })
  );

  return !hasBookedCategory;
};

/**
 * Hook to look at booked vendors in redux and determine if the mini quiz is
 * allowed.  It is allowed when the vendor category in question is not booked.
 */
export const useBookedVendorsAllowMiniQuiz = ({
  taxonomyKey,
}: {
  taxonomyKey?: VendorTaxonomyKey;
}) => {
  const userContext = useUserContext();

  const { isLoaded, bookableVendors } = useAppSelector((state) => state.bookedVendors);
  return useMemo(() => {
    if (userContext && userContext.is_guest) {
      return {
        loaded: true,
        miniQuizAllowed: false,
      };
    }

    if (isLoaded) {
      const bvAsArray = bookableVendors.allIds.map((id) => bookableVendors.byId[id]);
      const miniQuizAllowed = getBookedVendorsAllowsMiniQuiz(bvAsArray, taxonomyKey);

      return {
        loaded: true,
        miniQuizAllowed,
      };
    }

    return {
      loaded: false,
      miniQuizAllowed: false,
    };
  }, [bookableVendors.allIds, bookableVendors.byId, isLoaded, taxonomyKey, userContext]);
};
